<template>
  <v-main>
    <v-container fluid>
      <!-- Professional Form Card -->
      <v-row>
        <v-col cols="12">
          <v-card elevation="3" class="form-card">
            <!-- Page Header integrated into Form Card -->
            <v-card-title class="page-header py-2 px-3">
              <v-row class="w-100 ma-0" align="center">
                <!-- Left Side: Title and Description -->
                <v-col cols="12" md="8" class="pa-0">
                  <div class="d-flex align-center">
                    <div>
                      <h1 class="page-title primary--text">Create New Prescription</h1>
                      <p class="page-subtitle grey--text text--darken-1 mb-0">
                        Create a comprehensive medical prescription with detailed items and instructions
                      </p>
                    </div>
                  </div>
                </v-col>

                <!-- Right Side: Back Button and Form Badge -->
                <v-col cols="12" md="4" class="pa-0">
                  <div class="d-flex align-center justify-end">
                    <v-chip color="info" outlined x-small class="mr-2">
                      <v-icon left x-small>mdi-prescription</v-icon>
                      New Prescription Form
                    </v-chip>
                    <v-btn
                      color="grey darken-1"
                      outlined
                      @click="$router.back()"
                      class="back-button"
                    >
                      <v-icon left>mdi-arrow-left</v-icon>
                      Back to Prescriptions
                    </v-btn>
                  </div>
                </v-col>
              </v-row>
            </v-card-title>

            <v-divider></v-divider>

            <!-- Form Content -->
            <v-card-text class="form-content px-2 py-2">
              <v-form ref="form" v-model="valid" lazy-validation>
                
                <!-- Basic Information Section -->
                <div class="form-section mb-2">
                  <h3 class="section-title mb-1">
                    <v-icon color="primary" class="mr-2">mdi-account-details</v-icon>
                    Basic Information
                  </h3>
                  
                  <v-row>
                    <v-col cols="12" md="6">
                      <v-autocomplete
                        v-model="form.consultation_id"
                        :items="consultations"
                        label="Consultation (Optional)"
                        placeholder="Select related consultation"
                        outlined
                        :error-messages="errors.consultation_id"
                        clearable
                        @input="onConsultationChange"
                        class="professional-field"
                        hide-details="auto"
                      >
                        <template v-slot:prepend-inner>
                          <v-icon color="grey">mdi-medical-bag</v-icon>
                        </template>
                      </v-autocomplete>
                    </v-col>
                    <v-col cols="12" md="6">
                      <v-autocomplete
                        v-model="form.doctor_id"
                        :items="doctors"
                        label="Doctor"
                        placeholder="Select prescribing doctor"
                        outlined
                        :error-messages="errors.doctor_id"
                        clearable
                        class="professional-field"
                        hide-details="auto"
                      >
                        <template v-slot:prepend-inner>
                          <v-icon color="grey">mdi-doctor</v-icon>
                        </template>
                      </v-autocomplete>
                    </v-col>
                  </v-row>

                  <v-row>
                    <v-col cols="12" md="6">
                      <v-autocomplete
                        v-model="form.patient_id"
                        :items="patients"
                        label="Patient"
                        placeholder="Select patient"
                        outlined
                        :error-messages="errors.patient_id"
                        clearable
                        class="professional-field"
                        hide-details="auto"
                      >
                        <template v-slot:prepend-inner>
                          <v-icon color="grey">mdi-account-heart</v-icon>
                        </template>
                      </v-autocomplete>
                    </v-col>
                    <v-col cols="12" md="6">
                      <v-card outlined class="status-card">
                        <v-card-text class="pa-2">
                          <div class="d-flex align-center">
                            <v-icon color="grey" class="mr-3">mdi-prescription</v-icon>
                            <div class="flex-grow-1">
                              <div class="font-weight-medium mb-1">Prescription Status</div>
                              <div class="text-caption grey--text">Set the initial status for this prescription</div>
                            </div>
                            <v-switch
                              v-model="form.is_active"
                              color="success"
                              inset
                              hide-details
                              class="ml-3"
                            ></v-switch>
                          </div>
                          <div class="mt-2">
                            <v-chip 
                              small 
                              :color="form.is_active ? 'success' : 'warning'"
                              :text-color="form.is_active ? 'white' : 'black'"
                            >
                              {{ form.is_active ? 'Active' : 'Inactive' }}
                            </v-chip>
                          </div>
                        </v-card-text>
                      </v-card>
                    </v-col>
                  </v-row>
                </div>

                <!-- Medical Information Section -->
                <div class="form-section mb-2">
                  <h3 class="section-title mb-1">
                    <v-icon color="primary" class="mr-2">mdi-clipboard-pulse</v-icon>
                    Medical Information
                  </h3>
                  
                  <v-row>
                    <v-col cols="12">
                      <v-textarea
                        v-model="form.symptoms"
                        label="Symptoms *"
                        placeholder="Describe the patient's symptoms..."
                        outlined
                        rows="3"
                        :error-messages="errors.symptoms"
                        class="professional-field"
                        hide-details="auto"
                        required
                      >
                        <template v-slot:prepend-inner>
                          <v-icon color="grey">mdi-account-injury</v-icon>
                        </template>
                      </v-textarea>
                    </v-col>
                  </v-row>

                  <v-row>
                    <v-col cols="12">
                      <v-textarea
                        v-model="form.diagnosis"
                        label="Diagnosis *"
                        placeholder="Provide the medical diagnosis..."
                        outlined
                        rows="3"
                        :error-messages="errors.diagnosis"
                        class="professional-field"
                        hide-details="auto"
                        required
                      >
                        <template v-slot:prepend-inner>
                          <v-icon color="grey">mdi-clipboard-pulse</v-icon>
                        </template>
                      </v-textarea>
                    </v-col>
                  </v-row>

                  <v-row>
                    <v-col cols="12">
                      <v-textarea
                        v-model="form.tips"
                        label="Medical Tips & Recommendations"
                        placeholder="Provide medical tips, recommendations, and care instructions..."
                        outlined
                        rows="4"
                        :error-messages="errors.tips"
                        class="professional-field"
                        hide-details="auto"
                      >
                        <template v-slot:prepend-inner>
                          <v-icon color="grey">mdi-lightbulb-on</v-icon>
                        </template>
                      </v-textarea>
                    </v-col>
                  </v-row>
                </div>

                <!-- Prescription Items Section -->
                <div class="form-section mb-2">
                  <h3 class="section-title mb-1">
                    <v-icon color="primary" class="mr-2">mdi-pill</v-icon>
                    Prescription Items
                  </h3>
                  
                  <v-card outlined class="prescription-items-card">
                    <v-card-text class="pa-2">
                      <div
                        v-for="(item, index) in form.prescription_items"
                        :key="index"
                        class="prescription-item mb-3"
                      >
                        <div class="d-flex align-center mb-2">
                          <v-chip small color="primary" outlined>
                            <v-icon left x-small>mdi-numeric</v-icon>
                            Item {{ index + 1 }}
                          </v-chip>
                          <v-spacer></v-spacer>
                          <v-btn
                            color="error"
                            icon
                            small
                            @click="removePrescriptionItem(index)"
                            :disabled="form.prescription_items.length <= 1"
                          >
                            <v-icon small>mdi-delete</v-icon>
                          </v-btn>
                        </div>
                        
                        <v-row>
                          <v-col cols="12" md="6">
                            <v-autocomplete
                              v-model="item.product_id"
                              :items="products"
                              item-text="name"
                              item-value="id"
                              label="Product *"
                              placeholder="Select medication/product"
                              outlined
                              :error-messages="getPrescriptionItemError(index, 'product_id')"
                              class="professional-field"
                              hide-details="auto"
                            >
                              <template v-slot:prepend-inner>
                                <v-icon color="grey">mdi-pill</v-icon>
                              </template>
                            </v-autocomplete>
                          </v-col>
                          <v-col cols="12" md="6">
                            <v-autocomplete
                              v-model="item.mode_id"
                              :items="modes"
                              item-text="description"
                              item-value="id"
                              label="Administration Mode"
                              placeholder="Select mode"
                              outlined
                              :error-messages="getPrescriptionItemError(index, 'mode_id')"
                              class="professional-field"
                              hide-details="auto"
                            >
                              <template v-slot:prepend-inner>
                                <v-icon color="grey">mdi-format-list-bulleted</v-icon>
                              </template>
                            </v-autocomplete>
                          </v-col>
                        </v-row>

                        <v-row>
                          <v-col cols="12" md="4">
                            <v-text-field
                              v-model.number="item.dosage"
                              label="Dosage (mg)"
                              placeholder="Enter dosage"
                              outlined
                              type="number"
                              min="0"
                              step="0.01"
                              :error-messages="getPrescriptionItemError(index, 'dosage')"
                              class="professional-field"
                              hide-details="auto"
                            >
                              <template v-slot:prepend-inner>
                                <v-icon color="grey">mdi-medical-bag</v-icon>
                              </template>
                            </v-text-field>
                          </v-col>
                          <v-col cols="12" md="4">
                            <v-text-field
                              v-model="item.frequency"
                              label="Frequency"
                              placeholder="e.g., 3 times daily"
                              outlined
                              :error-messages="getPrescriptionItemError(index, 'frequency')"
                              class="professional-field"
                              hide-details="auto"
                            >
                              <template v-slot:prepend-inner>
                                <v-icon color="grey">mdi-clock</v-icon>
                              </template>
                            </v-text-field>
                          </v-col>
                          <v-col cols="12" md="4">
                            <v-text-field
                              v-model="item.duration"
                              label="Duration"
                              placeholder="e.g., 7 days"
                              outlined
                              :error-messages="getPrescriptionItemError(index, 'duration')"
                              class="professional-field"
                              hide-details="auto"
                            >
                              <template v-slot:prepend-inner>
                                <v-icon color="grey">mdi-calendar</v-icon>
                              </template>
                            </v-text-field>
                          </v-col>
                        </v-row>

                        <v-row>
                          <v-col cols="12">
                            <v-textarea
                              v-model="item.notes"
                              label="Special Instructions / Notes"
                              placeholder="Special instructions for this medication..."
                              outlined
                              rows="2"
                              :error-messages="getPrescriptionItemError(index, 'notes')"
                              class="professional-field"
                              hide-details="auto"
                            >
                              <template v-slot:prepend-inner>
                                <v-icon color="grey">mdi-note-text</v-icon>
                              </template>
                            </v-textarea>
                          </v-col>
                        </v-row>

                        <v-divider v-if="index < form.prescription_items.length - 1" class="mt-3"></v-divider>
                      </div>

                      <div class="d-flex align-center justify-between mt-3">
                        <v-btn
                          color="primary"
                          outlined
                          @click="addPrescriptionItem"
                          :disabled="form.prescription_items.length >= 20"
                          class="action-button"
                        >
                          <v-icon left>mdi-plus</v-icon>
                          Add Prescription Item
                        </v-btn>
                        <small class="text--secondary">
                          {{ form.prescription_items.length }}/20 items
                        </small>
                      </div>
                    </v-card-text>
                  </v-card>
                </div>

              </v-form>
            </v-card-text>

            <!-- Form Actions Footer -->
            <v-divider></v-divider>
            <v-card-actions class="form-actions pa-2">
              <v-row class="w-100 ma-0" align="center">
                <v-col cols="12" md="8" class="pa-0">
                  <div class="d-flex align-center">
                    <v-chip 
                      small 
                      :color="hasValidationErrors() ? 'error' : 'success'"
                      :text-color="hasValidationErrors() ? 'white' : 'white'"
                      class="mr-3"
                    >
                      <v-icon left x-small>
                        {{ hasValidationErrors() ? 'mdi-alert-circle' : 'mdi-check-circle' }}
                      </v-icon>
                      {{ hasValidationErrors() ? 'Please fix validation errors' : 'Form validation passed' }}
                    </v-chip>
                    <span class="text-caption grey--text">
                      All required fields must be completed
                    </span>
                  </div>
                </v-col>
                
                <v-col cols="12" md="4" class="pa-0">
                  <div class="d-flex justify-end">
                    <v-btn
                      color="grey"
                      outlined
                      class="mr-3 action-button"
                      @click="$router.back()"
                      :disabled="isSaving"
                    >
                      Cancel
                    </v-btn>
                    <v-btn
                      color="primary"
                      class="action-button"
                      @click="saveData"
                      :loading="isSaving"
                      :disabled="isSaving"
                    >
                      <v-icon left>mdi-content-save</v-icon>
                      Save Prescription
                    </v-btn>
                  </div>
                </v-col>
              </v-row>
            </v-card-actions>
          </v-card>
        </v-col>
      </v-row>

      <!-- Success Dialog -->
      <v-dialog v-model="showSuccessDialog" width="600">
        <v-card rounded="xxl">
          <v-container>
            <v-row align="center">
              <v-col sm="3" align="center">
                <v-icon
                  large
                  style="color: green; font-size: 90px; width: 200px !important"
                >
                  mdi-check-circle
                </v-icon>
              </v-col>
              <v-col sm="9" align="center">
                <v-card-text>
                  <v-row>
                    <v-col>
                      <h1 style="color: green">Success!</h1>
                      <br />
                      <h3>Prescription created successfully.</h3>
                      <br />
                      <v-btn
                        color="success"
                        @click="
                          showSuccessDialog = false;
                          $router.push('/prescriptions');
                        "
                      >
                        OK
                      </v-btn>
                    </v-col>
                  </v-row>
                </v-card-text>
              </v-col>
            </v-row>
          </v-container>
        </v-card>
      </v-dialog>
    </v-container>
  </v-main>
</template>

<script>
import axios from "@/axios";

export default {
  name: "PrescriptionCreatePage",
  data() {
    return {
      valid: false,
      form: {
        consultation_id: null,
        doctor_id: null,
        patient_id: null,
        symptoms: "",
        diagnosis: "",
        tips: "",
        is_active: true,
        prescription_items: [
          {
            product_id: null,
            mode_id: null,
            dosage: null,
            frequency: "",
            duration: "",
            notes: "",
            is_active: true,
          },
        ],
      },
      errors: {},
      isSaving: false,
      showSuccessDialog: false,
      consultations: [],
      doctors: [],
      patients: [],
      products: [],
      modes: [],
    };
  },
  mounted() {
    this.getMasters();
  },
  methods: {
    hasValidationErrors() {
      return Object.keys(this.errors).length > 0 || !this.valid;
    },

    async getMasters() {
      try {
        const response = await axios.get("masters?context=prescriptions");
        if (response.data?.users) {
          this.doctors = response.data.users
            .filter((user) => user.position?.name?.toLowerCase() === "doctor")
            .map((user) => ({
              text: user.first_name,
              value: user.id,
            }));
        }
        if (response.data?.users) {
          this.patients = response.data.users
            .filter((user) => user.position?.name?.toLowerCase() === "patient")
            .map((user) => ({
              text: user.first_name,
              value: user.id,
            }));
        }
        if (response.data?.consultations) {
          this.consultations = response.data.consultations.map(
            (consultation) => ({
              text: consultation.title,
              value: consultation.id,
            })
          );
        }
        if (response.data?.products) {
          this.products = response.data.products;
        }
        if (response.data?.modes) {
          this.modes = response.data.modes.map(mode => ({
            id: mode.id,
            name: mode.description,
            value: mode.id,
            text: mode.description
          }));
        }
      } catch (error) {
        console.error("Error fetching masters:", error);
      }
    },

    onConsultationChange() {
      // If a consultation is selected, auto-fill doctor and patient if available
      if (this.form.consultation_id) {
        const selectedConsultation = this.consultations.find(
          (c) => c.id === this.form.consultation_id
        );
        if (selectedConsultation) {
          this.form.doctor_id = selectedConsultation.doctor_id || null;
          this.form.patient_id = selectedConsultation.patient_id || null;
        }
      }
    },

    // Prescription Items Methods
    addPrescriptionItem() {
      if (this.form.prescription_items.length < 20) {
        this.form.prescription_items.push({
          product_id: null,
          mode_id: null,
          dosage: null,
          frequency: "",
          duration: "",
          notes: "",
          is_active: true,
        });
      }
    },

    removePrescriptionItem(index) {
      if (this.form.prescription_items.length > 1) {
        this.form.prescription_items.splice(index, 1);
      }
    },

    getPrescriptionItemError(index, field) {
      if (this.errors && this.errors[`prescription_items.${index}.${field}`]) {
        return this.errors[`prescription_items.${index}.${field}`];
      }
      return [];
    },

    async saveData() {
      this.isSaving = true;
      this.errors = {};

      try {
        const prescriptionData = {
          consultation_id: this.form.consultation_id,
          doctor_id: this.form.doctor_id,
          patient_id: this.form.patient_id,
          symptoms: this.form.symptoms,
          diagnosis: this.form.diagnosis,
          tips: this.form.tips,
          is_active: this.form.is_active,
          prescription_items: this.form.prescription_items.filter(
            (item) =>
              item.product_id ||
              item.dosage ||
              item.frequency ||
              item.duration ||
              item.notes
          ),
        };

        const response = await axios.post("/prescriptions", prescriptionData);

        if (response.data.success) {
          this.showSuccessDialog = true;
          this.resetForm();
        } else {
          this.$store.dispatch("errors/setError", {
            message:
              response.data.message ||
              "An error occurred while creating the prescription.",
          });
        }
      } catch (error) {
        console.error(error);

        if (error.response) {
          if (error.response.status === 422) {
            // Validation errors
            this.errors = error.response.data.errors || {};
          } else if (error.response.status === 401) {
            // Authentication error
            this.$store.dispatch("errors/setError", {
              message: "Authentication required. Please login again.",
            });
            this.$router.push("/login");
          } else {
            // Other errors
            this.$store.dispatch("errors/setError", {
              message:
                error.response.data.message ||
                "An error occurred while creating the prescription.",
            });
          }
        } else {
          // Network or other errors
          this.$store.dispatch("errors/setError", {
            message:
              "Network error. Please check your connection and try again.",
          });
        }
      } finally {
        this.isSaving = false;
      }
    },

    resetForm() {
      this.form = {
        consultation_id: null,
        doctor_id: null,
        patient_id: null,
        symptoms: "",
        diagnosis: "",
        tips: "",
        is_active: true,
        prescription_items: [
          {
            product_id: null,
            mode_id: null,
            dosage: null,
            frequency: "",
            duration: "",
            notes: "",
            is_active: true,
          },
        ],
      };
      this.errors = {};
    },
  },
};
</script>

<style scoped>
/* Professional Form Card with Integrated Header */
.form-card {
  background: #ffffff;
  border-radius: 8px;
  overflow: hidden;
  border-left: 4px solid var(--v-primary-base);
}

.page-header {
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  border-bottom: 1px solid #dee2e6;
}

.page-title {
  font-size: 1.25rem;
  font-weight: 600;
  line-height: 1.1;
  margin-bottom: 0.15rem;
}

.page-subtitle {
  font-size: 0.75rem;
  line-height: 1.2;
}

.back-button {
  min-width: 100px;
  height: 32px;
  font-weight: 500;
  text-transform: none;
  font-size: 0.75rem;
}

.form-content {
  background: #ffffff;
}

/* Section Styling */
.form-section {
  border-bottom: 1px solid #f0f0f0;
  padding-bottom: 0.5rem;
}

.form-section:last-child {
  border-bottom: none;
  margin-bottom: 0;
  padding-bottom: 0;
}

.section-title {
  font-size: 0.9rem;
  font-weight: 600;
  color: var(--v-primary-base);
  display: flex;
  align-items: center;
  margin-bottom: 0.5rem;
}

/* Professional Form Fields */
.professional-field >>> .v-input__control {
  min-height: 40px;
}

.professional-field >>> .v-text-field__details {
  padding-top: 4px;
}

.professional-field >>> .v-input__slot {
  border-radius: 4px;
}

.professional-field >>> .v-text-field input {
  font-size: 0.75rem;
  padding-top: 8px;
  padding-bottom: 8px;
}

.professional-field >>> .v-label {
  font-size: 0.75rem;
  font-weight: 500;
}

.professional-field >>> .v-icon {
  color: #6c757d !important;
}

.professional-field >>> .v-textarea textarea {
  font-size: 0.75rem;
  line-height: 1.4;
}

/* Status Card */
.status-card {
  border: 2px solid #e9ecef;
  border-radius: 8px;
  transition: all 0.2s ease;
}

.status-card:hover {
  border-color: var(--v-primary-base);
  background-color: #f8f9fa;
}

/* Prescription Items Card */
.prescription-items-card {
  border: 2px solid #e9ecef;
  border-radius: 8px;
  background: #fafafa;
}

.prescription-item {
  background: #ffffff;
  border: 1px solid #e9ecef;
  border-radius: 6px;
  padding: 1rem;
}

/* Form Actions */
.form-actions {
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  border-top: 1px solid #dee2e6;
}

.action-button {
  min-width: 90px;
  height: 32px;
  font-weight: 500;
  text-transform: none;
  border-radius: 4px;
  font-size: 0.75rem;
}

/* Spacing and Alignment */
.v-row {
  margin-left: -6px;
  margin-right: -6px;
}

.v-row > .v-col {
  padding: 0 6px;
}

/* Professional Color Scheme */
.v-card {
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08) !important;
}

.v-btn.v-btn--outlined {
  border: 2px solid currentColor;
}

.v-chip {
  font-weight: 500;
}

/* Responsive Design */
@media (max-width: 960px) {
  .page-title {
    font-size: 1.1rem;
  }
  
  .section-title {
    font-size: 0.85rem;
  }
  
  .form-content {
    padding: 0.75rem !important;
  }
  
  .back-button,
  .action-button {
    min-width: 80px;
    font-size: 0.7rem;
  }
}

@media (max-width: 600px) {
  .v-row > .v-col {
    padding: 0 4px;
  }
  
  .form-content {
    padding: 0.5rem !important;
  }
  
  .section-title {
    font-size: 0.8rem;
  }
  
  .professional-field >>> .v-input__control {
    min-height: 36px;
  }
  
  .professional-field >>> .v-text-field input {
    font-size: 0.7rem;
    padding-top: 6px;
    padding-bottom: 6px;
  }
  
  .back-button,
  .action-button {
    min-width: 70px;
    height: 28px;
    font-size: 0.65rem;
  }
}

/* Clean Focus States */
.professional-field >>> .v-input--is-focused .v-input__slot {
  border-color: var(--v-primary-base) !important;
  border-width: 2px !important;
}

/* Consistent Margins */
.mb-1 {
  margin-bottom: 0.5rem !important;
}

.mb-2 {
  margin-bottom: 0.75rem !important;
}

.mb-3 {
  margin-bottom: 1rem !important;
}

/* Professional Transitions */
.v-card,
.v-btn,
.professional-field,
.status-card,
.prescription-items-card {
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
}
</style>